'use strict';

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

const formatMoney = (cents) => {
  if (window.theme && window.theme.moneyFormat) {
    return window.theme.moneyFormat
      .replace('{{amount}}', (cents / 100).toFixed(2))
      .replace('{{amount_with_comma_separator}}', (cents / 100).toFixed(2).replace('.', ','))
      .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString());
  }
  return '₹\u202f' + (cents / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const debounce = (fn, ms) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

class RoProductPage {
  constructor(sectionId) {
    this.id = sectionId;
    this.el = $(`[data-ro-section-id="${sectionId}"]`);
    if (!this.el) return;

    this.variants      = this._parseJSON(`#RoProductVariants-${sectionId}`);
    this.mediaData     = this._parseJSON(`#RoProductMedia-${sectionId}`);
    this.productId     = this.el.dataset.productId;
    this.productUrl    = this.el.dataset.productUrl;
    this.productHandle = this.el.dataset.productHandle;

    if (!this.variants) return;

    this.selectedOptions = {};
    this.currentVariant  = null;
    this.isAddingToCart  = false;

    this._initVariantPicker();
    this._initQuantity();
    this._initGallery();
    this._initLightbox();
    this._initSizeGuide();
    this._initAddToCart();
    this._initWishlist();
    this._initShippingCheck();
    this._initAccordions();
    this._initMobileStickyBar();
    this._initSelectFirstAvailable();
  }

  _parseJSON(selector) {
    const el = $(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  _getEl(id) {
    return $(`#${id}-${this.id}`, this.el) || $(`[data-ro-id="${id}-${this.id}"]`, this.el);
  }

  /* ── Variant Picker ── */
  _initVariantPicker() {
    const optionBtns = $$('[data-ro-option-btn]', this.el);
    const colorOptionName = this._getColorOptionName();

    optionBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('ro-size-btn--unavailable')) return;

        const optionName  = btn.dataset.optionName;
        const optionValue = btn.dataset.optionValue;

        const isColor = btn.dataset.optionType === 'color' || 
          (colorOptionName && optionName.toLowerCase() === colorOptionName.toLowerCase());

        this.selectedOptions[optionName] = optionValue;

        $$(`[data-option-name="${optionName}"][data-ro-option-btn]`, this.el).forEach(b => {
          b.classList.remove('ro-color-btn--active', 'ro-size-btn--active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add(btn.dataset.optionType === 'color' ? 'ro-color-btn--active' : 'ro-size-btn--active');
        btn.setAttribute('aria-pressed', 'true');

        const labelEl = $(`[data-ro-option-selected="${optionName}"]`, this.el);
        if (labelEl) labelEl.textContent = optionValue;

        this._resolveVariant({ scrollGallery: isColor });
      });
    });
  }

  _resolveVariant(options = { scrollGallery: false }) {
    const matched = this.variants.find(v => {
      return v.options.every((val, idx) => {
        const optName = this._getOptionNameByIndex(idx);
        if (!optName) return true;
        const sel = this.selectedOptions[optName];
        return !sel || sel === val;
      });
    });

    this.currentVariant = matched || null;
    this._updateVariantUI(options);
  }

  _getOptionNameByIndex(idx) {
    const names = this.el.dataset.productOptions ? JSON.parse(this.el.dataset.productOptions) : [];
    return names[idx] || null;
  }

  _updateVariantUI(options = { scrollGallery: false }) {
    const v = this.currentVariant;

    const hiddenId = this._getEl('VariantId');
    if (hiddenId) {
      hiddenId.value = v ? v.id : '';
      hiddenId.dispatchEvent(new Event('change', { bubbles: true }));
      hiddenId.dispatchEvent(new Event('input', { bubbles: true }));
    }
    const form = this._getEl('ProductForm');
    if (form) form.dispatchEvent(new Event('change', { bubbles: true }));

    const dynamicCheckout = $('[data-ro-dynamic-checkout]', this.el);
    if (dynamicCheckout) dynamicCheckout.style.display = (v && v.available) ? '' : 'none';

    const skuEl = $('[data-ro-sku]', this.el);
    if (skuEl) {
      skuEl.textContent = v?.sku || '—';
      const skuWrapper = $('[data-ro-sku-wrap]', this.el);
      if (skuWrapper) skuWrapper.style.display = v?.sku ? '' : 'none';
    }

    this._updatePrice(v);
    this._updateAvailability(v);
    this._updateSizeAvailability();

    if (options.scrollGallery && v?.featured_media?.id) {
      this._scrollToVariantMedia(v.featured_media.id);
    }

    this._updateStickyBarPrice(v);
    this._hideOptionError();
  }

  _updatePrice(variant) {
    const saleEl    = $('[data-ro-price-sale]', this.el);
    const compareEl = $('[data-ro-price-compare]', this.el);
    const discEl    = $('[data-ro-price-discount]', this.el);

    if (!variant) {
      if (saleEl) saleEl.textContent = '—';
      if (compareEl) compareEl.style.display = 'none';
      if (discEl) discEl.style.display = 'none';
      return;
    }

    if (saleEl) saleEl.textContent = formatMoney(variant.price);

    if (compareEl && variant.compare_at_price > variant.price) {
      compareEl.textContent = formatMoney(variant.compare_at_price);
      compareEl.style.display = '';
    } else if (compareEl) {
      compareEl.style.display = 'none';
    }

    if (discEl && variant.compare_at_price > variant.price) {
      const pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
      discEl.textContent = `${pct}% OFF`;
      discEl.style.display = '';
    } else if (discEl) {
      discEl.style.display = 'none';
    }
  }

  _updateAvailability(variant) {
    const atcBtn  = this._getEl('AddToCart');
    const atcText = $('[data-ro-atc-text]', this.el);
    const availMsg = $('[data-ro-availability]', this.el);

    if (!variant) {
      if (atcBtn)  atcBtn.disabled = true;
      if (atcText) atcText.textContent = 'Select Options';
      if (availMsg) availMsg.style.display = 'none';
      return;
    }

    const available = variant.available;
    if (atcBtn)  atcBtn.disabled = !available;
    if (atcText) atcText.textContent = available ? 'Add to Cart' : 'Sold Out';

    if (availMsg) {
      if (!available) {
        availMsg.textContent = 'This variant is currently out of stock.';
        availMsg.classList.add('ro-atc-availability--visible');
      } else {
        availMsg.classList.remove('ro-atc-availability--visible');
      }
    }
  }

  _updateSizeAvailability() {
    const colorOption = this._getColorOptionName();
    const selectedColor = colorOption ? this.selectedOptions[colorOption] : null;

    $$('[data-ro-option-btn][data-option-type="size"]', this.el).forEach(btn => {
      const sizeVal = btn.dataset.optionValue;
      const sizeName = btn.dataset.optionName;

      const matchingVariant = this.variants.find(v => {
        return v.options.every((val, idx) => {
          const optName = this._getOptionNameByIndex(idx);
          if (optName === sizeName) return val === sizeVal;
          if (optName === colorOption && selectedColor) return val === selectedColor;
          return true;
        });
      });

      if (!matchingVariant) {
        btn.classList.add('ro-size-btn--unavailable');
        btn.disabled = true;
      } else if (!matchingVariant.available) {
        btn.classList.remove('ro-size-btn--unavailable');
        btn.classList.add('ro-size-btn--sold-out');
        btn.disabled = false;
      } else {
        btn.classList.remove('ro-size-btn--unavailable', 'ro-size-btn--sold-out');
        btn.disabled = false;
      }
    });
  }

  _getColorOptionName() {
    const names = this._getProductOptionNames();
    return names.find(n => /colou?r/i.test(n)) || null;
  }

  _getSizeOptionName() {
    const names = this._getProductOptionNames();
    return names.find(n => /size/i.test(n)) || null;
  }

  _getProductOptionNames() {
    try {
      return this.el.dataset.productOptions ? JSON.parse(this.el.dataset.productOptions) : [];
    } catch (e) {
      return [];
    }
  }

  _updateStickyBarPrice(variant) {
    const saleEl    = $('[data-ro-sticky-price]', this.el);
    const compareEl = $('[data-ro-sticky-compare]', this.el);
    if (!saleEl) return;
    saleEl.textContent = variant ? formatMoney(variant.price) : '';
    if (compareEl) {
      compareEl.textContent = (variant?.compare_at_price > variant?.price) ? formatMoney(variant.compare_at_price) : '';
      compareEl.style.display = (variant?.compare_at_price > variant?.price) ? '' : 'none';
    }
  }

  _initSelectFirstAvailable() {
    const firstAvail = this.variants.find(v => v.available) || this.variants[0];
    if (!firstAvail) return;

    const optionNames = this._getProductOptionNames();

    firstAvail.options.forEach((val, idx) => {
      const optName = optionNames[idx];
      if (!optName) return;
      this.selectedOptions[optName] = val;

      const btn = $(`[data-ro-option-btn][data-option-name="${optName}"][data-option-value="${CSS.escape(val)}"]`, this.el);
      if (btn) {
        btn.classList.add(btn.dataset.optionType === 'color' ? 'ro-color-btn--active' : 'ro-size-btn--active');
        btn.setAttribute('aria-pressed', 'true');
      }

      const labelEl = $(`[data-ro-option-selected="${optName}"]`, this.el);
      if (labelEl) labelEl.textContent = val;
    });

    this.currentVariant = firstAvail;
    this._updateVariantUI({ scrollGallery: true });
  }

  _showOptionError(message) {
    const el = $('[data-ro-option-error]', this.el);
    if (!el) return;
    el.textContent = message;
    el.classList.add('ro-option-error--visible');
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  _hideOptionError() {
    const el = $('[data-ro-option-error]', this.el);
    if (el) el.classList.remove('ro-option-error--visible');
  }

  /* ── Quantity ── */
  _initQuantity() {
    const decBtn = $('[data-ro-qty-dec]', this.el);
    const incBtn = $('[data-ro-qty-inc]', this.el);
    const input  = $('[data-ro-qty-input]', this.el);
    if (!decBtn || !incBtn || !input) return;

    const update = () => {
      const val = parseInt(input.value, 10) || 1;
      input.value = Math.max(1, val);
      decBtn.disabled = parseInt(input.value, 10) <= 1;

      const formQty = this._getEl('FormQuantity');
      if (formQty) {
        formQty.value = input.value;
        formQty.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    decBtn.addEventListener('click', () => {
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      update();
    });
    incBtn.addEventListener('click', () => {
      input.value = Math.min(99, (parseInt(input.value, 10) || 1) + 1);
      update();
    });
    input.addEventListener('change', update);
    update();
  }

  /* ── Gallery ── */
  _initGallery() {
    const slider  = $('[data-ro-gallery-slider]', this.el);
    const thumbs  = $$('[data-ro-thumb]', this.el);
    const dots    = $$('[data-ro-gallery-dot]', this.el);
    const slides  = $$('[data-ro-gallery-slide]', this.el);
    const prevBtn = $('[data-ro-gallery-prev]', this.el);
    const nextBtn = $('[data-ro-gallery-next]', this.el);

    if (!slider) return;

    let hasDragged = false;

    // Lightbox click trigger
    slides.forEach((item, idx) => {
      item.addEventListener('click', (e) => {
        if (hasDragged) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        this._openLightbox(idx);
      });
      item.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._openLightbox(idx);
        }
      });
    });

    // Thumbnails click
    thumbs.forEach((thumb, idx) => {
      thumb.addEventListener('click', () => this._slideToIndex(idx));
    });

    // Dots click
    dots.forEach((dot, idx) => {
      dot.addEventListener('click', () => this._slideToIndex(idx));
    });

    // Desktop Arrows
    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const currentIdx = typeof this._activeSlideIndex === 'number'
          ? this._activeSlideIndex
          : Math.round(slider.scrollLeft / slider.offsetWidth);
        const targetIdx = (currentIdx - 1 + slides.length) % slides.length;
        this._slideToIndex(targetIdx);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const currentIdx = typeof this._activeSlideIndex === 'number'
          ? this._activeSlideIndex
          : Math.round(slider.scrollLeft / slider.offsetWidth);
        const targetIdx = (currentIdx + 1) % slides.length;
        this._slideToIndex(targetIdx);
      });
    }

    // Scroll listener - Updates active dot and thumbnail
    const onScroll = debounce(() => {
      const galleryEl = $('[data-ro-product-gallery]', this.el);
      if (galleryEl?.dataset.thumbPosition === 'grid_2' && window.innerWidth >= 1024) return;
      const idx = Math.round(slider.scrollLeft / slider.offsetWidth);
      this._activeSlideIndex = idx;
      this._setActiveMediaIndex(idx);
    }, 30);

    slider.addEventListener('scroll', onScroll, { passive: true });

    // Desktop Only: Mouse drag-to-scroll
    let isMouseDown = false;
    let startX = 0;
    let initialScrollLeft = 0;

    slider.addEventListener('mousedown', (e) => {
      if (window.innerWidth < 1024) return; // Mobile par touch scroll chale, mouse logic nahi
      const galleryEl = $('[data-ro-product-gallery]', this.el);
      if (galleryEl?.dataset.thumbPosition === 'grid_2') return;

      isMouseDown = true;
      hasDragged = false;
      slider.classList.add('ro-is-dragging');
      startX = e.pageX - slider.offsetLeft;
      initialScrollLeft = slider.scrollLeft;
    });

    window.addEventListener('mousemove', (e) => {
      if (!isMouseDown) return;
      const currentX = e.pageX - slider.offsetLeft;
      const walk = (currentX - startX);
      if (Math.abs(walk) > 6) {
        hasDragged = true;
      }
      slider.scrollLeft = initialScrollLeft - walk;
    });

    window.addEventListener('mouseup', () => {
      if (!isMouseDown) return;
      isMouseDown = false;
      slider.classList.remove('ro-is-dragging');

      if (hasDragged) {
        const slideWidth = slider.offsetWidth;
        const targetIdx = Math.round(slider.scrollLeft / slideWidth);
        this._slideToIndex(targetIdx);
        setTimeout(() => { hasDragged = false; }, 60);
      }
    });
  }

  _slideToIndex(idx) {
    const slider = $('[data-ro-gallery-slider]', this.el);
    if (!slider) return;
    this._activeSlideIndex = idx;
    slider.scrollTo({ left: slider.offsetWidth * idx, behavior: 'smooth' });
    this._setActiveMediaIndex(idx);
  }

  _setActiveMediaIndex(idx) {
    const thumbs = $$('[data-ro-thumb]', this.el);
    const dots   = $$('[data-ro-gallery-dot]', this.el);

    thumbs.forEach((t, i) => {
      const isActive = i === idx;
      t.classList.toggle('ro-gallery-thumb--active', isActive);
      t.setAttribute('aria-selected', String(isActive));
      if (isActive) {
        t.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    });

    dots.forEach((d, i) => {
      d.classList.toggle('ro-gallery-dot--active', i === idx);
      d.setAttribute('aria-selected', String(i === idx));
    });
  }

  _scrollToVariantMedia(mediaId) {
    const slider = $('[data-ro-gallery-slider]', this.el);
    if (!slider) return;

    const slides = $$('[data-ro-gallery-slide]', this.el);
    const targetSlide = slides.find(s => s.dataset.mediaId === String(mediaId));
    if (!targetSlide) return;

    const galleryEl = $('[data-ro-product-gallery]', this.el);
    const thumbPos = galleryEl?.dataset.thumbPosition;
    const isDesktopGrid = thumbPos === 'grid_2' && window.innerWidth >= 1024;

    slides.forEach(s => s.classList.toggle('ro-gallery-slider__slide--active-media', s === targetSlide));

    if (isDesktopGrid) {
      targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
      const idx = slides.indexOf(targetSlide);
      if (idx > -1) {
        this._slideToIndex(idx);
      }
    }
  }

  /* ── Lightbox ── */
  _initLightbox() {
    this._lbImages = [];
    this._lbIndex  = 0;
    this._lbOpen   = false;

    $$('[data-ro-gallery-item]', this.el).forEach(item => {
      const img = item.querySelector('img');
      if (img) {
        this._lbImages.push({
          src: img.dataset.lbSrc || img.src,
          alt: img.alt || '',
        });
      }
    });

    const lb       = $('[data-ro-lightbox]', this.el);
    const overlay  = $('[data-ro-lightbox-overlay]', this.el);
    const closeBtn = $('[data-ro-lightbox-close]', this.el);
    const prevBtn  = $('[data-ro-lightbox-prev]', this.el);
    const nextBtn  = $('[data-ro-lightbox-next]', this.el);
    if (!lb) return;

    this._lbEl        = lb;
    this._lbImgEl     = $('[data-ro-lightbox-img]', lb);
    this._lbCounterEl = $('[data-ro-lightbox-counter]', lb);

    // Eagerly preload all gallery images in background for instant 0ms switching
    this._preloadLightboxImages();

    overlay?.addEventListener('click', () => this._closeLightbox());
    closeBtn?.addEventListener('click', () => this._closeLightbox());
    prevBtn?.addEventListener('click', () => this._lbNavigate(-1));
    nextBtn?.addEventListener('click', () => this._lbNavigate(1));

    document.addEventListener('keydown', e => {
      if (!this._lbOpen) return;
      if (e.key === 'Escape')     this._closeLightbox();
      if (e.key === 'ArrowLeft')  this._lbNavigate(-1);
      if (e.key === 'ArrowRight') this._lbNavigate(1);
    });

    let touchStartX = 0;
    lb.addEventListener('touchstart', e => {
      touchStartX = e.changedTouches[0].clientX;
    }, { passive: true });
    lb.addEventListener('touchend', e => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) this._lbNavigate(diff > 0 ? 1 : -1);
    }, { passive: true });
  }

  _preloadLightboxImages() {
    if (!this._lbImages || !this._lbImages.length) return;
    const preload = () => {
      this._lbImages.forEach(img => {
        if (img.src) {
          const preloader = new Image();
          preloader.decoding = 'async';
          preloader.src = img.src;
        }
      });
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(preload);
    } else {
      setTimeout(preload, 100);
    }
  }

  _preloadAdjacentLightbox(idx) {
    const total = this._lbImages.length;
    if (total <= 1) return;
    const nextIdx = (idx + 1) % total;
    const prevIdx = (idx - 1 + total) % total;
    [nextIdx, prevIdx].forEach(i => {
      const src = this._lbImages[i]?.src;
      if (src) {
        const p = new Image();
        p.decoding = 'async';
        p.src = src;
      }
    });
  }

  _openLightbox(idx) {
    if (!this._lbEl || !this._lbImages.length) return;
    this._lbOpen  = true;
    this._lbIndex = Math.max(0, Math.min(idx, this._lbImages.length - 1));
    this._renderLightboxImage();
    this._lbEl.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    $('[data-ro-lightbox-close]', this._lbEl)?.focus();
    this._preloadAdjacentLightbox(this._lbIndex);
  }

  _closeLightbox() {
    this._lbOpen = false;
    this._lbEl.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  _lbNavigate(dir) {
    const total = this._lbImages.length;
    if (total === 0) return;
    this._lbIndex = (this._lbIndex + dir + total) % total;
    this._renderLightboxImage();
    this._preloadAdjacentLightbox(this._lbIndex);
  }

  _renderLightboxImage() {
    const img = this._lbImages[this._lbIndex];
    if (this._lbImgEl && img) {
      this._lbImgEl.src = img.src;
      this._lbImgEl.alt = img.alt || '';
    }
    if (this._lbCounterEl) {
      this._lbCounterEl.textContent = `${this._lbIndex + 1} / ${this._lbImages.length}`;
    }
  }

  /* ── Size Guide Modal ── */
  _initSizeGuide() {
    const openBtn  = $('[data-ro-size-guide-open]', this.el);
    const modal    = $('[data-ro-size-guide-modal]', this.el);
    const overlay  = $('[data-ro-size-guide-overlay]', this.el);
    const closeBtn = $('[data-ro-size-guide-close]', this.el);
    if (!modal) return;

    this._sgOpen = false;

    const open = () => {
      this._sgOpen = true;
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      closeBtn?.focus();
    };
    const close = () => {
      this._sgOpen = false;
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      openBtn?.focus();
    };

    openBtn?.addEventListener('click', open);
    closeBtn?.addEventListener('click', close);
    overlay?.addEventListener('click', close);
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this._sgOpen) close();
    });
  }

  /* ── Add to Cart ── */
  _initAddToCart() {
    const atcBtn = this._getEl('AddToCart');
    if (!atcBtn) return;

    atcBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (this.isAddingToCart) return;
      await this._handleAddToCart();
    });
  }

  async _handleAddToCart() {
    const colorOpt = this._getColorOptionName();
    if (colorOpt && !this.selectedOptions[colorOpt]) {
      this._showOptionError('Please select a colour.');
      return;
    }

    const sizeOpt = this._getSizeOptionName();
    if (sizeOpt && !this.selectedOptions[sizeOpt]) {
      this._showOptionError('Please select a size.');
      return;
    }

    const optionNames = this._getProductOptionNames();
    for (const opt of optionNames) {
      if (!this.selectedOptions[opt]) {
        this._showOptionError(`Please select a ${opt.toLowerCase()}.`);
        return;
      }
    }

    if (!this.currentVariant) {
      this._showOptionError('This combination is currently unavailable.');
      return;
    }

    if (!this.currentVariant.available) {
      this._showOptionError('This variant is currently out of stock.');
      return;
    }

    const qtyInput = $('[data-ro-qty-input]', this.el);
    const qty = Math.max(1, parseInt(qtyInput?.value || 1, 10));

    const hiddenId = this._getEl('VariantId');
    if (hiddenId) hiddenId.value = this.currentVariant.id;
    const formQty = this._getEl('FormQuantity');
    if (formQty) formQty.value = qty;

    const atcBtn  = this._getEl('AddToCart');
    const atcText = $('[data-ro-atc-text]', this.el);
    this.isAddingToCart = true;
    if (atcBtn) {
      atcBtn.disabled = true;
      atcBtn.classList.add('ro-btn--loading');
    }

    try {
      const form = this._getEl('ProductForm');
      const formData = form ? new FormData(form) : new FormData();

      formData.set('id', this.currentVariant.id);
      formData.set('quantity', qty);
      formData.append('sections[]', 'cart-count-badge');
      formData.append('sections[]', 'offcanvas-cart');
      formData.append('sections_url', window.location.pathname);

      const cartContainer = document.querySelector('cart-container');

      if (cartContainer && typeof cartContainer.add === 'function') {
        await cartContainer.add(formData);
      } else {
        const addUrl = (window.Shopify?.routes?.cart_add_url || '/cart/add') + '.js';
        const resp = await fetch(addUrl, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });

        const data = await resp.json();
        if (!resp.ok) throw new Error(data.description || 'Could not add item to cart.');

        window.dispatchEvent(new CustomEvent('kt.cart.added', { detail: data }));
        if (typeof window.reloadCartElements === 'function' && data.sections) {
          window.reloadCartElements(data.sections);
        }
        this._openExistingCartDrawer();
      }

      if (atcBtn && atcText) {
        atcBtn.classList.add('ro-btn--success');
        atcText.textContent = 'Added to Bag ✓';
        setTimeout(() => {
          atcBtn.classList.remove('ro-btn--success');
          atcText.textContent = this.currentVariant?.available ? 'Add to Cart' : 'Sold Out';
        }, 1800);
      }

    } catch (err) {
      const errMsg = err.message || 'An error occurred. Please try again.';
      if (typeof window.showCartError === 'function') {
        window.showCartError(errMsg);
      } else {
        this._showToast(errMsg, 'error');
      }
      console.error('[RoProductPage] Add to cart error:', err);
    } finally {
      this.isAddingToCart = false;
      if (atcBtn) {
        atcBtn.disabled = !this.currentVariant?.available;
        atcBtn.classList.remove('ro-btn--loading');
      }
    }
  }

  _openExistingCartDrawer() {
    const offcanvas = document.querySelector('#offcanvas-cart');
    if (offcanvas && window.bootstrap?.Offcanvas) {
      window.bootstrap.Offcanvas.getOrCreateInstance(offcanvas).show();
      return;
    }
    window.dispatchEvent(new CustomEvent('kt.cart.open'));
  }

  /* ── Wishlist (Synchronized with store-wide ks-wishlist & header badge) ── */
  _initWishlist() {
    const btn    = $('[data-ro-wishlist-btn]', this.el);
    const textEl = $('[data-ro-wishlist-text]', this.el);
    if (!btn) return;

    const rawUrl = this.productUrl || window.location.pathname;
    const cleanUrl = rawUrl.split('?')[0].replace(/\/$/, '');
    const handle = (this.productHandle || cleanUrl.split('/products/')[1] || '').trim().toLowerCase();

    const getKsWishlist = () => {
      try {
        const raw = JSON.parse(localStorage.getItem('ks-wishlist') || '[]');
        return Array.isArray(raw) ? raw : [];
      } catch {
        return [];
      }
    };

    const isWishlisted = () => {
      const list = getKsWishlist();
      return list.some(item => {
        if (!item) return false;
        if (item.url && item.url.split('?')[0].replace(/\/$/, '') === cleanUrl) return true;
        if (item.handle && handle && String(item.handle).toLowerCase() === handle) return true;
        return false;
      });
    };

    const updateUI = (wishlisted) => {
      btn.classList.toggle('ro-wishlist-btn--active', wishlisted);
      btn.classList.toggle('active', wishlisted);
      btn.setAttribute('aria-pressed', String(wishlisted));
      if (textEl) {
        textEl.textContent = wishlisted ? 'Wishlisted' : 'Add to Wishlist';
      }
    };

    // Initial check
    updateUI(isWishlisted());

    const syncBadges = () => {
      const count = getKsWishlist().length;
      document.querySelectorAll('.wishlist-count-badge').forEach(badge => {
        badge.dataset.count = count;
        badge.textContent = count;
      });
    };

    const customWishlistEl = btn.closest('wishlist-button');

    if (!customWishlistEl) {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const container = document.querySelector('wishlist-container');
        if (container && typeof container.setProduct === 'function') {
          await container.setProduct(cleanUrl);
        } else {
          let list = getKsWishlist();
          const nowWishlisted = isWishlisted();
          if (nowWishlisted) {
            list = list.filter(item => {
              const u = item.url ? item.url.split('?')[0].replace(/\/$/, '') : '';
              return u !== cleanUrl && (!handle || String(item.handle).toLowerCase() !== handle);
            });
          } else {
            try {
              const res = await fetch(`${cleanUrl}.js`);
              const prod = await res.json();
              list.push({
                url: cleanUrl,
                id: prod.id,
                handle: prod.handle,
                title: prod.title,
                img_src: prod.featured_image,
                img_alt: prod.featured_image?.alt || '',
                compare_at_price: prod.compare_at_price,
                price: prod.price,
                price_varies: prod.price_varies,
                added_at: Date.now()
              });
            } catch {
              list.push({
                url: cleanUrl,
                id: this.productId || '',
                handle: handle,
                title: document.title,
                added_at: Date.now()
              });
            }
          }
          localStorage.setItem('ks-wishlist', JSON.stringify(list));
          syncBadges();
          if (container && typeof container.setContent === 'function') {
            container.setContent();
          }
        }

        const nextState = isWishlisted();
        updateUI(nextState);
        this._showToast(nextState ? 'Added to wishlist ♥' : 'Removed from wishlist');
      });
    } else {
      // When wrapped inside <wishlist-button>, the custom element invokes container.setProduct()
      // Listen to click to reflect state & toast once updated
      btn.addEventListener('click', () => {
        setTimeout(() => {
          const nextState = isWishlisted();
          updateUI(nextState);
          syncBadges();
          this._showToast(nextState ? 'Added to wishlist ♥' : 'Removed from wishlist');
        }, 120);
      });
    }

    // Sync on external storage changes (e.g., product removed from header drawer or another tab)
    window.addEventListener('storage', (e) => {
      if (e.key === 'ks-wishlist') {
        updateUI(isWishlisted());
        syncBadges();
      }
    });

    // Observer for external classes toggled by wishlist.js
    const observer = new MutationObserver(() => {
      const active = btn.classList.contains('active') || btn.classList.contains('ro-wishlist-btn--active');
      if (textEl) {
        textEl.textContent = active ? 'Wishlisted' : 'Add to Wishlist';
      }
    });
    observer.observe(btn, { attributes: true, attributeFilter: ['class', 'aria-pressed'] });
  }

  /* ── Shipping Checker ── */
  _initShippingCheck() {
    const checkBtn = $('[data-ro-pincode-check]', this.el);
    const input    = $('[data-ro-pincode-input]', this.el);
    const result   = $('[data-ro-pincode-result]', this.el);
    if (!checkBtn || !input || !result) return;

    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 6);
    });

    const show = (msg, type) => {
      result.textContent = msg;
      result.className = `ro-shipping-check__result ro-shipping-check__result--${type}`;
    };

    const validate = () => {
      const pin = input.value.trim();
      if (!pin) { show('Please enter a PIN code.', 'error'); return; }
      if (!/^\d{6}$/.test(pin)) { show('Enter a valid 6-digit PIN code.', 'error'); return; }
      const first = parseInt(pin[0], 10);
      if (first >= 1 && first <= 6) {
        show('✓ Delivery available in 3–5 business days.', 'success');
      } else {
        show('Delivery not available at this PIN code.', 'error');
      }
    };

    checkBtn.addEventListener('click', validate);
    input.addEventListener('keydown', e => { if (e.key === 'Enter') validate(); });
  }

  /* ── Accordions ── */
  _initAccordions() {
    $$('[data-ro-accordion]', this.el).forEach(accordion => {
      const trigger = accordion.querySelector('[data-ro-accordion-trigger]');
      const body    = accordion.querySelector('[data-ro-accordion-body]');
      if (!trigger || !body) return;

      const hasContent = body.textContent.trim().length > 0 || 
                         body.querySelector('img, svg, iframe, video, table') !== null;
      if (!hasContent) {
        accordion.style.display = 'none';
        return;
      }

      const isInitiallyOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isInitiallyOpen) {
        trigger.setAttribute('aria-expanded', 'true');
        body.removeAttribute('hidden');
        body.style.display = 'block';
      } else {
        trigger.setAttribute('aria-expanded', 'false');
        body.setAttribute('hidden', '');
        body.style.display = 'none';
      }

      trigger.addEventListener('click', () => {
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
          trigger.setAttribute('aria-expanded', 'false');
          body.style.height = `${body.offsetHeight}px`;
          body.style.overflow = 'hidden';

          void body.offsetHeight;

          body.style.transition = 'height 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease';
          body.style.height = '0px';
          body.style.opacity = '0';

          const onCollapseEnd = (e) => {
            if (e.propertyName !== 'height') return;
            body.removeEventListener('transitionend', onCollapseEnd);
            if (trigger.getAttribute('aria-expanded') === 'false') {
              body.setAttribute('hidden', '');
              body.style.display = 'none';
              body.style.height = '';
              body.style.overflow = '';
              body.style.transition = '';
              body.style.opacity = '';
            }
          };
          body.addEventListener('transitionend', onCollapseEnd);

        } else {
          trigger.setAttribute('aria-expanded', 'true');
          body.removeAttribute('hidden');

          body.style.display = 'block';
          body.style.height = 'auto';
          body.style.visibility = 'hidden';

          const trueHeight = body.scrollHeight;

          body.style.visibility = '';
          body.style.height = '0px';
          body.style.overflow = 'hidden';
          body.style.opacity = '0';

          void body.offsetHeight;

          body.style.transition = 'height 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease';
          body.style.height = `${trueHeight}px`;
          body.style.opacity = '1';

          const onExpandEnd = (e) => {
            if (e.propertyName !== 'height') return;
            body.removeEventListener('transitionend', onExpandEnd);
            if (trigger.getAttribute('aria-expanded') === 'true') {
              body.style.height = '';
              body.style.overflow = '';
              body.style.transition = '';
              body.style.opacity = '';
            }
          };
          body.addEventListener('transitionend', onExpandEnd);
        }
      });
    });
  }

  /* ── Mobile Sticky Bar (Handled by sections/sticky-atc.liquid) ── */
  _initMobileStickyBar() {
    // Replaced by sections/sticky-atc.liquid
    return;
  }

  /* ── Toast Messages ── */
  _showToast(message, type = '') {
    let toast = $('[data-ro-toast]', this.el);
    if (!toast) {
      toast = document.createElement('div');
      toast.setAttribute('data-ro-toast', '');
      toast.className = 'ro-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }

    clearTimeout(this._toastTimer);
    toast.textContent = message;
    toast.className = `ro-toast${type ? ` ro-toast--${type}` : ''}`;
    void toast.offsetHeight;
    toast.classList.add('ro-toast--visible');
    this._toastTimer = setTimeout(() => {
      toast.classList.remove('ro-toast--visible');
    }, 3200);
  }
}

/* ── Section Lifecycle Initializer ── */
const roSectionInstances = new Map();

function initRoSection(el) {
  const sectionId = el.dataset.roSectionId;
  if (!sectionId) return;
  if (roSectionInstances.has(sectionId)) {
    const prev = roSectionInstances.get(sectionId);
    if (prev && typeof prev.destroy === 'function') prev.destroy();
  }
  const instance = new RoProductPage(sectionId);
  roSectionInstances.set(sectionId, instance);
}

document.addEventListener('DOMContentLoaded', () => {
  $$('[data-ro-section-id]').forEach(initRoSection);
});

document.addEventListener('shopify:section:load', e => {
  const el = e.target.querySelector('[data-ro-section-id]') || (e.target.matches('[data-ro-section-id]') ? e.target : null);
  if (el) initRoSection(el);
});

document.addEventListener('shopify:section:unload', e => {
  const el = e.target.querySelector('[data-ro-section-id]') || (e.target.matches('[data-ro-section-id]') ? e.target : null);
  if (el && roSectionInstances.has(el.dataset.roSectionId)) {
    const inst = roSectionInstances.get(el.dataset.roSectionId);
    if (inst && typeof inst.destroy === 'function') inst.destroy();
    roSectionInstances.delete(el.dataset.roSectionId);
  }
});